import { useEffect, useRef, type RefObject } from 'react';
import './swipe.css';
import { DIRECTION_LOCK_PX, EDGE_GUARD_PX } from '@/hooks/gestureSlop';

/**
 * Swipe right = Archive, swipe left = Delete (ADR 0048, plan 15 §6.1).
 *
 * The hook renders nothing and the rows gain no handlers: the listeners are
 * delegated and a row is resolved with `closest('.message-row[data-index]')`,
 * the same way `MessageList` resolves right-click and long-press (ADR 0019).
 * They sit on the *document*, filtered to the given container, rather than on
 * the container itself — the element behind the ref is swapped whenever the
 * list goes from skeleton to rows, and a listener bound at mount would be left
 * on the one that went away.
 *
 * ## Who owns the gesture
 *
 * A finger on a row is a scroll until proven otherwise. The document is the
 * scroller on handheld, so the rows carry `touch-action: pan-y pinch-zoom`
 * (swipe.css): vertical pans and zoom stay the browser's, horizontal movement
 * is delivered to us. The decision is made once, on the first 8 px of
 * movement — `|dx| > |dy|` is a swipe, anything else is a scroll and the hook
 * stands down for the rest of that touch — and never revisited, so a swipe
 * cannot turn into a scroll halfway or the reverse. A `pointercancel` (the
 * browser took the touch after all) always springs back and never commits.
 *
 * Not started: within 24 px of the left edge (the browser's back gesture), by
 * a mouse (a desktop pointer has the context menu, the keymap and no scroll to
 * fight; a drag there is a text-selection habit, not a triage gesture), by a
 * second finger (a pinch), or in a direction with no action — `onArchive` is
 * left out in Trash, Junk and on an account with no archive folder.
 *
 * Crossing the 8 px also cancels `MessageList`'s long-press, which drops its
 * timer on any movement past the same slop; ADR 0019 asks for exactly that.
 *
 * ## Commit
 *
 * Past 40 % of the row's width a release slides the row out and then calls
 * the action with the row's *current* `data-index` — rows are keyed by message
 * id, so the element is still the same message even if a live update moved it.
 * The action is the same `archive`/`remove` the keymap uses, which is undoable
 * (`useMessageMutations`): the row leaves the list at once and the snackbar
 * offers Undo. Below 40 % the row springs back. The click a touch produces
 * after a swipe is swallowed so a swipe can never also open the message.
 *
 * Every swipe action has a non-gesture twin: `e`/`#` on the cursor row and
 * Archive/Delete in the context menu (right-click, long-press, Shift+F10).
 */

/** Fraction of the row's width past which a release commits. */
export const SWIPE_COMMIT_FRACTION = 0.4;

/** `--duration-normal`: how long the slide-out and the spring-back take. */
const SETTLE_MS = 200;
/** After the action, a row still on screen was not removed (refused, say) and comes back. */
const STRANDED_MS = 600;
const STRANDED_POLL_MS = 16;

export interface SwipeActionsOptions {
  /** Swipe right. Omit where archiving makes no sense; the right swipe then does not exist. */
  onArchive?: ((index: number) => void) | undefined;
  /** Swipe left. */
  onDelete?: ((index: number) => void) | undefined;
  /** Off on desktop, and wherever the list is not the thing being triaged. Default on. */
  enabled?: boolean | undefined;
}

interface Gesture {
  pointerId: number;
  startX: number;
  startY: number;
  row: HTMLElement;
  phase: 'undecided' | 'swiping';
  width: number;
  dx: number;
  underlay?: HTMLElement;
}

export function useSwipeActions(
  containerRef: RefObject<HTMLElement | null>,
  options: SwipeActionsOptions,
): void {
  const enabled = options.enabled ?? true;

  // The latest callbacks, read when a gesture ends — the listeners below are
  // bound once, not re-bound on every render of the list.
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });

  // Rows opt into `touch-action: pan-y` through this attribute (swipe.css).
  // No dependency list: the element behind the ref can change on any render.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;
    container.setAttribute('data-swipe-actions', '');
    return () => container.removeAttribute('data-swipe-actions');
  });

  useEffect(() => {
    if (!enabled) return;

    let gesture: Gesture | undefined;
    let swallowClick = false;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (fn: () => void, ms: number) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        fn();
      }, ms);
      timers.add(timer);
    };
    const reduceMotion = () =>
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const actionFor = (dx: number) => (dx > 0 ? latest.current.onArchive : latest.current.onDelete);

    const onPointerDown = (event: PointerEvent) => {
      // A second finger is a pinch, not a swipe.
      if (gesture) {
        if (gesture.phase === 'swiping') settle(gesture.row, gesture.underlay);
        gesture = undefined;
        return;
      }
      swallowClick = false;
      if (event.pointerType === 'mouse' || !event.isPrimary || event.button !== 0) return;
      if (event.clientX <= EDGE_GUARD_PX) return;
      const container = containerRef.current;
      if (!container || !(event.target instanceof Element)) return;
      const row = event.target.closest<HTMLElement>('.message-row[data-index]');
      if (!row || !container.contains(row)) return;
      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        row,
        phase: 'undecided',
        width: 0,
        dx: 0,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      const g = gesture;
      if (!g || event.pointerId !== g.pointerId) return;
      const dx = event.clientX - g.startX;
      const dy = event.clientY - g.startY;

      if (g.phase === 'undecided') {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < DIRECTION_LOCK_PX) return;
        // Vertical first: a scroll. Or sideways towards an action that does
        // not exist here. Either way this touch is not ours.
        if (Math.abs(dx) <= Math.abs(dy) || !actionFor(dx)) {
          gesture = undefined;
          return;
        }
        begin(g);
      }

      // Clamp at zero on a side with no action, so the row can be dragged
      // back but never reveals an underlay that promises nothing.
      const allowed = (dx > 0 && latest.current.onArchive) || (dx < 0 && latest.current.onDelete);
      g.dx = allowed ? dx : 0;
      paint(g);
    };

    const onPointerUp = (event: PointerEvent) => {
      const g = gesture;
      if (!g || event.pointerId !== g.pointerId) return;
      gesture = undefined;
      if (g.phase !== 'swiping') return;
      if (Math.abs(g.dx) >= g.width * SWIPE_COMMIT_FRACTION && actionFor(g.dx)) leave(g);
      else settle(g.row, g.underlay);
    };

    const onPointerCancel = (event: PointerEvent) => {
      const g = gesture;
      if (!g || event.pointerId !== g.pointerId) return;
      gesture = undefined;
      if (g.phase === 'swiping') settle(g.row, g.underlay);
    };

    const onClick = (event: MouseEvent) => {
      if (!swallowClick) return;
      swallowClick = false;
      // Capture phase on the document: stopped before React's root listener,
      // so the row's onClick (open the message) never runs.
      event.preventDefault();
      event.stopPropagation();
    };

    function begin(g: Gesture) {
      g.phase = 'swiping';
      g.width = g.row.getBoundingClientRect().width || containerRef.current?.clientWidth || 1;
      swallowClick = true;
      // A row still springing back from the last swipe starts from here.
      g.row.parentElement
        ?.querySelectorAll<HTMLElement>('.swipe-underlay')
        .forEach((node) => {
          if (node.nextElementSibling === g.row) node.remove();
        });
      g.underlay = createUnderlay(g.row);
      g.row.dataset.swipe = 'active';
    }

    function paint(g: Gesture) {
      g.row.style.setProperty('--swipe-x', `${g.dx}px`);
      const underlay = g.underlay;
      if (!underlay) return;
      // The virtualizer positions rows by transform; follow it if it moves.
      underlay.style.transform = g.row.style.transform;
      if (g.dx !== 0) underlay.dataset.action = g.dx > 0 ? 'archive' : 'delete';
      underlay.dataset.armed = String(Math.abs(g.dx) >= g.width * SWIPE_COMMIT_FRACTION);
    }

    function settle(row: HTMLElement, underlay: HTMLElement | undefined) {
      row.dataset.swipe = 'settling';
      row.style.setProperty('--swipe-x', '0px');
      if (underlay) underlay.dataset.armed = 'false';
      later(
        () => {
          // A new swipe on the same row owns it now.
          if (row.dataset.swipe !== 'settling') return;
          delete row.dataset.swipe;
          row.style.removeProperty('--swipe-x');
          underlay?.remove();
        },
        reduceMotion() ? 0 : SETTLE_MS,
      );
    }

    function leave(g: Gesture) {
      const { row, underlay } = g;
      const direction = Math.sign(g.dx);
      row.dataset.swipe = 'leaving';
      row.style.setProperty('--swipe-x', `${direction * g.width}px`);
      later(
        () => {
          const index = Number(row.dataset.index);
          const action = actionFor(direction);
          if (row.isConnected && Number.isInteger(index) && action) action(index);
          // The row leaves the DOM a render or two later. Take the band with
          // it as soon as it goes — left behind, it shows as a coloured seam
          // between the rows that close up over it.
          const deadline = Date.now() + STRANDED_MS;
          const watch = () => {
            if (!row.isConnected) underlay?.remove();
            else if (row.dataset.swipe !== 'leaving') return;
            else if (Date.now() >= deadline) settle(row, underlay);
            else later(watch, STRANDED_POLL_MS);
          };
          later(watch, 0);
        },
        reduceMotion() ? 0 : SETTLE_MS,
      );
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('click', onClick, true);
      for (const timer of timers) clearTimeout(timer);
      if (gesture?.phase === 'swiping') {
        delete gesture.row.dataset.swipe;
        gesture.row.style.removeProperty('--swipe-x');
        gesture.underlay?.remove();
      }
      gesture = undefined;
    };
  }, [containerRef, enabled]);
}

/* The two glyphs of `components/Icon.tsx`, drawn outside React. */
const GLYPHS = {
  archive: 'M3 7h18v3H3zM5 10v10h14V10M10 14h4',
  delete: 'M4 6h16M9 6V4h6v2M6 6l1 15h10l1-15M10 10v7M14 10v7',
} as const;

const SVG_NS = 'http://www.w3.org/2000/svg';

function glyph(action: keyof typeof GLYPHS): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'swipe-underlay__icon');
  svg.setAttribute('data-for', action);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('focusable', 'false');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', GLYPHS[action]);
  svg.append(path);
  return svg;
}

/**
 * The coloured band the row slides off. A sibling placed *before* the row
 * with the row's own transform and height, so it sits exactly underneath and
 * the row paints over it; it is decoration, hidden from assistive technology.
 */
function createUnderlay(row: HTMLElement): HTMLElement {
  const underlay = document.createElement('div');
  underlay.className = 'swipe-underlay';
  underlay.setAttribute('aria-hidden', 'true');
  underlay.dataset.armed = 'false';
  underlay.style.transform = row.style.transform;
  underlay.style.height = `${row.offsetHeight}px`;
  underlay.append(glyph('archive'), glyph('delete'));
  row.before(underlay);
  return underlay;
}
